import { useCallback, useRef, useState } from 'react';
import { InfiniteScrollOptions, InfiniteScrollReturn } from './useInfiniteScroll.types';
import { useWindowScroll } from './useWindowScroll';

/**
 * Хук для реализации бесконечной подгрузки данных при скролле
 * @param onLoadMore - Функция для загрузки следующей порции данных
 * @param options - Опции для настройки поведения хука
 * @returns Объект с состоянием и методами для управления бесконечной подгрузкой
 */
export const useInfiniteScroll = (
    onLoadMore: () => void | Promise<void>,
    options: InfiniteScrollOptions = {},
): InfiniteScrollReturn => {
    const { threshold = 100, enabled = true, hasMore: hasMoreProp } = options;

    const [hasMore] = useState(hasMoreProp);
    const containerRef = useRef<HTMLDivElement>(null);
    const isLoadingRef = useRef(false);

    // Функция для проверки, нужно ли загружать данные
    const checkIfShouldLoad = useCallback(() => {
        if (!enabled || !hasMore || isLoadingRef.current) {
            return false;
        }

        const container = containerRef.current;

        if (!container) {
            // Если контейнер не задан, используем window
            const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
            const windowHeight = window.innerHeight;
            const documentHeight = document.documentElement.scrollHeight;

            return scrollTop + windowHeight >= documentHeight - threshold;
        }

        // Если контейнер задан, проверяем его скролл
        const { scrollTop, scrollHeight, clientHeight } = container;

        return scrollTop + clientHeight >= scrollHeight - threshold;
    }, [enabled, hasMore, threshold]);

    // Обработчик загрузки данных
    const loadMore = useCallback(async () => {
        if (!enabled || !hasMore || !hasMoreProp || isLoadingRef.current) {
            return;
        }

        isLoadingRef.current = true;

        try {
            await onLoadMore();
        } catch (error) {
            console.error('Error loading more data:', error);
        } finally {
            isLoadingRef.current = false;
        }
    }, [enabled, hasMore, onLoadMore, hasMoreProp]);

    const handleScroll = useCallback(() => {
        if (checkIfShouldLoad()) {
            loadMore();
        }
    }, [checkIfShouldLoad, loadMore]);

    useWindowScroll(handleScroll, 100, { passive: true });

    return {
        loading: isLoadingRef.current,
        loadMore,
        hasMore,
        containerRef,
    };
};
